<template>
  <div class="view-short-course">
    <action-dispatcher
      action="courses/getMicrolearning"
      loading-class="my-5"
      :parameters="$route.params.id"
    >
      <header class="page-header">
        <div class="container py-5 text-light">
          <div class="page-title">
            <h5 class="text-primary">MasterClass</h5>
            <h4>{{ microlearning.title }}</h4>
          </div>

          <div class="mb-5">
            <video-player v-if="featuredVideo" :lesson="featuredVideo" />
          </div>

          <div v-if="caColor || caBlack" class="row pt-4">
            <div class="col-lg-5">
              <img
                :src="microlearning.ca_cover"
                class="img-fluid d-block mx-auto mb-5 mb-lg-0"
              />
            </div>
            <div class="col-lg-6 offset-lg-1">
              <h4 class="mb-4">
                <i class="fas fa-book-open mr-2"></i> Caderno de ativação
              </h4>
              <div v-html="microlearning.description" />
              <div class="d-flex flex-column align-items-start">
                <button
                  @click="read(caColor)"
                  class="btn btn-primary px-5 py-2 strong mb-3"
                >
                  LER CADERNO
                </button>
                <button @click="read(caBlack)" class="btn text-light">
                  <u>Ler versão preto e branco</u>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section
        v-if="shares.length === 1 || hasTranscription"
        class="bg-light-gray py-5"
      >
        <div class="container">
          <div class="row">
            <div v-if="shares.length === 1" class="col-lg-4 pb-5 pb-lg-0">
              <share :file="shares[0].file" />
            </div>

            <template v-if="hasTranscription">
              <div class="col-lg-4 offset-lg-1 col-6">
                <h5 class="mb-4">
                  <i class="far fa-file-alt mr-2"></i> Transcrições
                </h5>
                <img
                  class="img-fluid"
                  src="@/assets/images/guerrilha-way/transcricao.jpg"
                />
              </div>
              <div class="col-lg-3 pt-5 col-6">
                <transcriptions
                  :black="transcriptionBlack"
                  :color="transcriptionColor"
                  @read="read"
                />
              </div>
            </template>
          </div>
        </div>
      </section>

      <section v-if="videos.length" class="bg-light py-5">
        <div class="container">
          <h5><i class="fas fa-video mr-2 mb-3"></i> Vídeos</h5>
          <videos :videos="videos" />
        </div>
      </section>

      <section v-if="audios.length" class="bg-light-gray py-5">
        <div class="container">
          <h5><i class="fas fa-podcast mr-2 mb-3"></i> Audios da semana</h5>
          <audios :audios="audios" />
        </div>
      </section>

      <section v-if="shares.length > 1" class="bg-light py-5">
        <div class="container">
          <div class="row">
            <div
              v-for="(share, index) in shares"
              :key="index"
              class="col-lg-4 pb-5 pb-lg-0"
            >
              <share :file="share.file" />
            </div>
          </div>
        </div>
      </section>

      <section v-if="pdfs.length" class="bg-light py-5">
        <div class="container">
          <h5><i class="fas fa-file-pdf mr-2 mb-3"></i> PDFs</h5>
          <pdfs :pdfs="pdfs" />
        </div>
      </section>

      <transition name="fade">
        <pdf-reader
          v-if="reading"
          :resource="selectedFile"
          :lesson="lesson"
          @close="reading = false"
        />
      </transition>
    </action-dispatcher>
  </div>
</template>

<script>
import { mapState } from "vuex";
import get from "lodash/get";
import isMobile from "ismobilejs";
import PdfReader from "@/components/lesson/PdfReader";
import VideoPlayer from "@/components/lesson/VideoPlayer";
import Transcriptions from "@/components/lesson/Transcriptions";
import Videos from "@/components/lesson/Videos";
import Audios from "@/components/lesson/Audios";
import Pdfs from "@/components/lesson/Pdfs";
import Share from "@/components/lesson/Share";

export default {
  components: {
    PdfReader,
    VideoPlayer,
    Transcriptions,
    Videos,
    Audios,
    Pdfs,
    Share
  },
  data() {
    return {
      reading: false,
      selectedFile: null
    };
  },
  computed: {
    ...mapState("courses", ["microlearning"]),
    resources() {
      return get(this.microlearning, "resourses", []);
    },
    featuredVideo() {
      return this.resources.find(
        r => r.is_main && ["videos", "lives"].includes(r.category_key)
      );
    },
    shares() {
      return this.resources.filter(r => r.category_key === "share");
    },
    caBlack() {
      return this.resources.find(
        r => r.category_key === "ca" && r.is_blackwhite
      );
    },
    caColor() {
      return this.resources.find(
        r => r.category_key === "ca" && !r.is_blackwhite
      );
    },
    transcriptionBlack() {
      return this.resources.find(
        r => r.category_key === "transcription" && r.is_blackwhite
      );
    },
    transcriptionColor() {
      return this.resources.find(
        r => r.category_key === "transcription" && !r.is_blackwhite
      );
    },
    hasTranscription() {
      return this.transcriptionBlack || this.transcriptionColor;
    },
    videos() {
      return this.resources.filter(
        r => !r.is_main && ["videos", "lives"].includes(r.category_key)
      );
    },
    audios() {
      return this.resources.filter(r => r.category_key === "audios");
    },
    pdfs() {
      return this.resources.filter(r => r.category_key === "pdf");
    }
  },
  methods: {
    read(resource) {
      if (resource.external_link) {
        window.open(resource.external_link, "_blank");
      } else if (isMobile(window.navigator).any) {
        window.open(resource.file, "_blank");
      } else {
        this.selectedFile = resource;
        this.reading = true;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-short-course {
  .page-title {
    h5 {
      font-size: 18px;
      letter-spacing: 7px;
      text-transform: uppercase;
    }
    h6 {
    }
  }
}
</style>
